import React, { useEffect, useRef } from 'react';

/*
  A list with a panel beside it.

  Written 5 September 2026 for "The shared table", and it is the widest-reaching
  shape in OnHand rather than the table: **Group D drew ten setup screens and
  only one of them is a table.** The rest are this, a rail of panes, or a dialog.
  So the piece with the most reach is the list and panel, with the table as the
  special case.

  **What it settles, and it is the thing three screens each got slightly wrong.**

  - **Below 1024 the panel goes ABOVE the list.** Group D settled that and the
    running app does the opposite: `.ua-split` stacked list first, panel second,
    at 1180 and under. The thing you are looking at matters more than the list
    you picked it from, and on a phone the list can be a hundred rows deep, so
    the panel was below all hundred of them.
  - **The list scrolls inside itself** rather than growing the page, so the panel
    stays where it is while you look for the next row.
  - **The three screens that had this shape each wrote it out again**, and the
    Review Hub wrote a fourth on 5 September. Four copies of one shape is how
    six menus and three count-entry layouts happened.

  It is deliberately not clever: it lays out two halves, decides their order at a
  width, and keeps the row you picked in sight. What goes in them is the screen's
  business.

  `selected` is whatever identifies the row that is open. It is not read: it is
  only watched, so that when it changes the list scrolls its own scroller to the
  row wearing the open-row class. **That class is `on` or `is-on`**, because the
  two screens that had this shape spelled the same idea two ways and neither is
  worth a rename here. Only the list's own scroller moves; the page never does.
*/
export default function ListPanel({ list, panel, listWidth, empty, selected }) {
  const box = useRef(null);

  useEffect(() => {
    const b = box.current;
    if (!b) return;
    const row = b.querySelector('.on, .is-on');
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < b.scrollTop) b.scrollTop = top - 8;
    else if (bottom > b.scrollTop + b.clientHeight) b.scrollTop = bottom - b.clientHeight + 8;
  }, [selected]);

  return (
    <div className="lp" style={listWidth ? { '--lp-list': listWidth } : undefined}>
      <div className="lp-list" ref={box}>{list}</div>
      <div className="lp-panel">{panel || (empty && <div className="lp-empty">{empty}</div>)}</div>
    </div>
  );
}

/*
  Bringing the panel into view after a row is picked.

  **It is only ever right below 1024, and doing it on a desk is a fault.** On a
  desk the panel is already beside the list and on screen, so scrolling to it
  moves the page for no reason: it scrolled a warehouse's own title behind the
  page bar the first time this was photographed. Stacked, the panel is above the
  list, so tapping a row halfway down the list changes something that is off the
  top of the screen and nothing appears to happen.

  Group D's words were "nothing has to scroll on your behalf", and this is what
  is left of that: the app scrolls only when the two halves are stacked.
*/
export function revealPanel(el) {
  if (!el || typeof window === 'undefined') return;
  if (window.innerWidth > 1023) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
